/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { initialState, reducer } from '../../../reducers/generalData/containerReducer';
import { useToast } from 'hooks/toasts/useToast';
import ReportGeneralDataCard from './ReportGeneralDataCard';
import { useValidation } from '@totalsoft/pure-validations-react';
import { reportValidator } from 'features/reports/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { REPORT_TYPES_QUERY, OPEN_MODES_QUERY, REPORT_PARTNER_TYPES_QUERY, REPORT_DOCUMENT_TYPES_QUERY } from '../queries';
import { ADD_UPDATE_REPORT } from '../mutations';
import { REPORT_UPDATED } from '../subscriptions';

import useLanguageTracker from 'hooks/useLanguageTracker';

const ReportGeneralDataContainer = ({ data, reportId, onRedirect, onChangeReportItemTitle }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [reportValidation, validateReport] = useValidation(reportValidator);

  // #region Queries

  const { data: reportTypesData, refetch: refetchReportTypes } = useQuery(REPORT_TYPES_QUERY);

  const { data: openModesData, refetch: refetchOpenModes } = useQuery(OPEN_MODES_QUERY);

  const { data: reportPartnerTypesData, refetch: refetchReportPartnerTypes } = useQuery(REPORT_PARTNER_TYPES_QUERY);

  const { data: reportDocumentTypesData, refetch: refetchReportDocumentTypes } = useQuery(REPORT_DOCUMENT_TYPES_QUERY);

  // #endregion

  // #region Mutations

  const [addOrUpdateReport] = useMutation(ADD_UPDATE_REPORT);

  // #endregion

  // #region Subscriptions

  useSubscription(REPORT_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportUpdated'), 'success');
      if (reportId === undefined) onRedirect(data?.reportUpdated?.report.reportId);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedReports' });
      client.cache.gc();
    }
  });

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchReportTypes();
    refetchOpenModes();
    refetchReportPartnerTypes();
    refetchReportDocumentTypes();
  }, [refetchReportTypes, refetchOpenModes, refetchReportPartnerTypes, refetchReportDocumentTypes]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    onChangeReportItemTitle(localState.header.reportName);
  }, [localState.header.reportName, onChangeReportItemTitle]);

  useEffect(() => {
    validateReport(localState.header);
  }, [localState.header, validateReport]);

  // #endregion

  // #region Handlers

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };

  const handleSave = () => {
    if (!validateReport(localState.header)) return;
    const { changed, reportXPartnerTypesChanged, reportXDocumentTypesChanged, reportXPartnerTypeIds, reportXDocumentTypeIds, ...report } =
      localState.header;
    const input = { ...report };

    dispatch({ type: 'OnHeaderSaving' });
    return addOrUpdateReport({ variables: { input } });
  };

  // #endregion

  return (
    <ReportGeneralDataCard
      reportItem={localState.header}
      onPropertyChanged={handlePropertyChanged}
      onSave={handleSave}
      openModes={openModesData ? openModesData.openModes : []}
      reportTypes={reportTypesData ? reportTypesData.reportTypes : []}
      partnerTypes={reportPartnerTypesData ? reportPartnerTypesData.reportPartnerTypes : []}
      documentTypes={reportDocumentTypesData ? reportDocumentTypesData.reportDocumentTypes : []}
      validation={reportValidation}
    />
  );
};

ReportGeneralDataContainer.propTypes = {
  data: PropTypes.object.isRequired,
  reportId: PropTypes.number,
  onRedirect: PropTypes.func,
  onChangeReportItemTitle: PropTypes.func
};

export default ReportGeneralDataContainer;
